import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

import { axiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import PasswordTextField from "../../../common/components/PasswordTextField"
import TextLink from "../../../common/components/TextLink"
import { setErrorFromAPIArray } from "../../../common/utils"
import LoadingPaper from "../components/LoadingPaper"
import PageTitle from "../components/PageTitle"
import WatchHelpVideoLink from "../components/WatchHelpVideoLink"
import { useRedirectLoggedInUser } from "../utils"

export default function SignUp() {
  const [firstName, setFirstName] = useState("")
  const [firstNameError, setFirstNameError] = useState(null)
  const [lastName, setLastName] = useState("")
  const [lastNameError, setLastNameError] = useState(null)
  const [signupEmail, setSignupEmail] = useState("")
  const [signupEmailError, setSignupEmailError] = useState(null)
  const [password, setPassword] = useState("")
  const [passwordError, setPasswordError] = useState(null)
  const [password2, setPassword2] = useState("")
  const [password2Error, setPassword2Error] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const navigate = useNavigate()
  const searchParams = useSearchParams()[0]

  function handleFirstNameChanged(e) {
    setFirstName(e.target.value)
    setFirstNameError(null)
  }

  function handleLastNameChanged(e) {
    setLastName(e.target.value)
    setLastNameError(null)
  }

  function handleSignupEmailChanged(e) {
    setSignupEmail(e.target.value)
    setSignupEmailError(null)
  }

  function handlePasswordChanged(e) {
    setPassword(e.target.value.replace(/\s/g, ""))
    setPasswordError(null)
  }

  function handlePassword2Changed(e) {
    setPassword2(e.target.value.replace(/\s/g, ""))
    setPassword2Error(null)
  }

  function handleSignupButtonClicked() {
    setIsLoading(true)
    axiosClient
      .post("/users/api/signup/", {
        first_name: firstName,
        last_name: lastName,
        email: signupEmail,
        phone: searchParams.get("p"),
        city: searchParams.get("bct"),
        state: searchParams.get("bst"),
        brokerage_name: searchParams.get("bcmp"),
        highrise_customer_id: searchParams.get("hrid"),
        password_1: password,
        password_2: password2,
      })
      .then(() => navigate("/user/signup-success"))
      .catch(({ response: { data } }) => {
        setErrorFromAPIArray(data, "first_name", setFirstNameError)
        setErrorFromAPIArray(data, "last_name", setLastNameError)
        let emailError = data.email
        if (emailError) {
          if (emailError instanceof Array) {
            emailError = emailError[0]
          }
          setSignupEmailError(emailError)
        }
        let password1Error = data.password_1
        if (password1Error) {
          setPasswordError(mergeErrors(password1Error))
        }
        let password2Error = data.password_2
        if (password2Error) {
          setPassword2Error(mergeErrors(password2Error))
        }
      })
      .then(() => setIsLoading(false))
  }

  function mergeErrors(errors) {
    return errors.join("\n")
  }

  useRedirectLoggedInUser()

  useEffect(() => {
    setFirstName(searchParams.get("fn") || "")
    setLastName(searchParams.get("ln") || "")
    setSignupEmail(searchParams.get("e") || "")
  }, [searchParams])

  return (
    <Column>
      <PageTitle text="Create new account" />
      <LoadingPaper isLoading={isLoading}>
        <Column sx={{ gap: 2 }}>
          <TextField
            size="small"
            type="text"
            label="First Name"
            required
            value={firstName}
            onChange={handleFirstNameChanged}
            error={!!firstNameError}
            helperText={firstNameError}
            fullWidth
          />
          <TextField
            size="small"
            type="text"
            label="Last Name"
            required
            value={lastName}
            onChange={handleLastNameChanged}
            error={!!lastNameError}
            helperText={lastNameError}
            fullWidth
          />
          <TextField
            size="small"
            type="email"
            label="Email Address"
            required
            value={signupEmail}
            onChange={handleSignupEmailChanged}
            error={!!signupEmailError}
            helperText={signupEmailError}
            fullWidth
          />
          <PasswordTextField
            size="small"
            label="Password"
            required
            value={password}
            onChange={handlePasswordChanged}
            error={!!passwordError}
            helperText={passwordError}
            fullWidth
          />
          <PasswordTextField
            size="small"
            label="Confirm Password"
            required
            value={password2}
            onChange={handlePassword2Changed}
            error={!!password2Error}
            helperText={password2Error}
            fullWidth
          />
          <Button
            variant="contained"
            disabled={
              !firstName || !lastName || !signupEmail || !password || !password2
            }
            onClick={handleSignupButtonClicked}
            sx={{ mt: 2 }}
          >
            Sign Up
          </Button>
          <Typography variant="body1" textAlign="center">
            Already have an account?{" "}
            <TextLink to="/user/log-in" text="Log in" />
          </Typography>
          <WatchHelpVideoLink linkProps={{ sx: { textAlign: "center" } }} />
        </Column>
      </LoadingPaper>
    </Column>
  )
}
